// The metric registry — `data/tables/metrics.json` (REQ §7 glossary) typed and validated once at load.
// Explainers read `label` / `metaTerm` / `formula` / `definition` / `caveat` / `resultsBased`; alerts read
// `goodDirection` (ENGINE §10.2); the C# mirror reads the same file as an embedded resource (ARCH §2.4), so
// the table, not this file, is the source.
//
// ruleVersion 2 (T-237, D-103 / D-117): `definition`, `caveat` and `resultsBased` moved in from the app-side
// draft that `apps/web/src/explainers/metricCopy.ts` and its verbatim mobile copy carried, so the explainer
// on the phone and the explainer on the desktop cannot word the same metric two ways. `note` stays doc text
// for this document and the C# mirror and is never shown to a user.

import table from '../../data/tables/metrics.json';
import type { MetricKey } from '../types/alerts';

/** The rule version compiled into this side; `tools/check-mirror.mjs` fails when the table says otherwise. */
export const METRICS_TABLE_RULE_VERSION = 2;

/** Derived views the glossary names but the API's `MetricKey` (alert rules) does not. */
export type DerivedMetricKey =
  | 'clicksAll'
  | 'shareOfSpendPct'
  | 'shareOfSpendSameResultPct'
  | 'shareOfResultsPct'
  | 'efficiencyVsAccountPct';

export type MetricRegistryKey = MetricKey | DerivedMetricKey;

export type MetricUnit = 'money' | 'count' | 'pct' | 'ratio';
export type GoodDirection = 'up' | 'down' | 'neutral';

export interface MetricDefinition {
  key: MetricRegistryKey;
  /** The user's term — binding for UI copy (REQ §7). */
  label: string;
  /** Ads Manager's name and field, for "In Ads Manager this is called …"; empty for metrics that are ours. */
  metaTerm: string;
  metaField: string;
  unit: MetricUnit;
  goodDirection: GoodDirection;
  /** A member of the API's `MetricKey` union — usable in an alert rule. */
  alertable: boolean;
  /** The glossary's formula text, shown in the explainer VERBATIM (REQ-043 AC3). */
  formula: string;
  /** One sentence: what it means and, unless the direction is neutral, which way is better (REQ-043 AC3). */
  definition: string;
  /** What counts, or the caveat — one sentence in the user's words. Empty when the metric has none. */
  caveat: string;
  /** REQ-043 AC4: its explainer also states the result definition (REQ-260) and how results are counted. */
  resultsBased: boolean;
  /** Doc text for this table and the C# mirror — never shown to a user. */
  note: string;
}

const ALERTABLE_KEYS: readonly MetricKey[] = [
  'spend',
  'results',
  'resultValue',
  'costPerResult',
  'roas',
  'impressions',
  'reach',
  'frequency',
  'linkClicks',
  'clickRatePct',
  'costPerClick',
  'cpm',
  'conversionRatePct',
  'budgetUsePct',
  'pacingPct',
  'forecastVsPlanPct',
];
const DERIVED_KEYS: readonly DerivedMetricKey[] = [
  'clicksAll',
  'shareOfSpendPct',
  'shareOfSpendSameResultPct',
  'shareOfResultsPct',
  'efficiencyVsAccountPct',
];
const UNITS: readonly MetricUnit[] = ['money', 'count', 'pct', 'ratio'];
const DIRECTIONS: readonly GoodDirection[] = ['up', 'down', 'neutral'];

const KNOWN_KEYS = new Set<string>([...ALERTABLE_KEYS, ...DERIVED_KEYS]);

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value);
}

function fail(row: unknown, why: string): never {
  throw new Error(`metrics.json: ${why} — ${JSON.stringify(row)}`);
}

/** Validate one raw row of the table. Exported so a test can prove the checks on bad rows. */
export function parseMetricDefinition(raw: unknown): MetricDefinition {
  if (typeof raw !== 'object' || raw === null) fail(raw, 'a row must be an object');
  const r = raw as Record<string, unknown>;
  if (!KNOWN_KEYS.has(String(r.key))) fail(raw, `unknown metric key "${String(r.key)}"`);
  const key = r.key as MetricRegistryKey;
  if (!isOneOf(UNITS, r.unit)) fail(raw, `unit must be one of ${UNITS.join(' | ')}`);
  if (!isOneOf(DIRECTIONS, r.goodDirection))
    fail(raw, `goodDirection must be one of ${DIRECTIONS.join(' | ')}`);
  for (const field of [
    'label',
    'metaTerm',
    'metaField',
    'formula',
    'definition',
    'caveat',
    'note',
  ] as const)
    if (typeof r[field] !== 'string') fail(raw, `${field} must be a string`);
  if (typeof r.label !== 'string' || r.label.length === 0) fail(raw, 'label must not be empty');
  if (typeof r.formula !== 'string' || r.formula.length === 0)
    fail(raw, 'formula must not be empty');
  if (typeof r.definition !== 'string' || r.definition.length === 0)
    fail(raw, 'definition must not be empty');
  if (typeof r.resultsBased !== 'boolean') fail(raw, 'resultsBased must be a boolean');
  const alertable = (ALERTABLE_KEYS as readonly string[]).includes(key);
  if (r.alertable !== alertable)
    fail(raw, `alertable must be ${alertable} for "${key}" (API MetricKey)`);
  return {
    key,
    label: r.label,
    metaTerm: r.metaTerm as string,
    metaField: r.metaField as string,
    unit: r.unit,
    goodDirection: r.goodDirection,
    alertable,
    formula: r.formula,
    definition: r.definition,
    caveat: r.caveat as string,
    resultsBased: r.resultsBased,
    note: r.note as string,
  };
}

/** Parse a whole table object. Every known key must appear exactly once; `ruleVersion` must match. */
export function parseMetricsTable(raw: unknown): ReadonlyMap<MetricRegistryKey, MetricDefinition> {
  if (typeof raw !== 'object' || raw === null) throw new Error('metrics.json: not an object');
  const t = raw as { ruleVersion?: unknown; metrics?: unknown };
  if (t.ruleVersion !== METRICS_TABLE_RULE_VERSION)
    throw new Error(
      `metrics.json: ruleVersion ${String(t.ruleVersion)} but this build compiles ${METRICS_TABLE_RULE_VERSION}`,
    );
  if (!Array.isArray(t.metrics)) throw new Error('metrics.json: "metrics" must be an array');
  const map = new Map<MetricRegistryKey, MetricDefinition>();
  for (const row of t.metrics) {
    const def = parseMetricDefinition(row);
    if (map.has(def.key)) throw new Error(`metrics.json: duplicate key "${def.key}"`);
    map.set(def.key, def);
  }
  for (const key of KNOWN_KEYS)
    if (!map.has(key as MetricRegistryKey))
      throw new Error(`metrics.json: missing row for "${key}"`);
  return map;
}

const registry = parseMetricsTable(table);

/** Every metric the product names, in table order. */
export const metricRegistry: readonly MetricDefinition[] = [...registry.values()];

/** The keys of the registry, in table order. */
export const METRIC_KEYS: readonly MetricRegistryKey[] = metricRegistry.map((m) => m.key);

export function isMetricKey(key: string): key is MetricRegistryKey {
  return registry.has(key as MetricRegistryKey);
}

/** The definition of one metric. Throws on an unknown key — a typo is a bug, not a missing label. */
export function metricDefinition(key: MetricRegistryKey): MetricDefinition {
  const def = registry.get(key);
  if (!def) throw new Error(`metrics.json has no row for "${String(key)}"`);
  return def;
}

/**
 * Which figure a metric's number IS — an observed `value` or a user-set `limit` (a threshold, a plan, a
 * guardrail). The two differ in ONE place and only for money: a measured cost reads §8.5 row 2
 * (`money.cost`, minor units below 1,000) and an amount the owner typed reads row 4 (`money.perDay`, whole
 * when it is whole), because "$25" is what they put in the field and "$25.00" is not (API §1.7, 0.3.13).
 */
export type MetricFigureRole = 'value' | 'limit';

/**
 * The ONE derivation of a metric's own format context (API §1.7 / ARCH §8.5), used by two callers that must
 * never disagree: `@sl/core/messages`' renderer, which reads it for a `{limit:metric.limit}` /
 * `{value:metric.value}` slot off the message's own `metric` param (0.3.13, T-383), and the demo server,
 * which used to hold a private copy of it and formatted the figure into a `text` param before sending.
 *
 * It is a `FormatContext` by its spelling and not by its type: `@sl/core/format` imports nothing from here
 * and this module imports nothing from there, so the registry stays the metrics table's and only the table's
 * (ARCH §8.1). `messages/render.ts` is where the string meets `FORMAT_CONTEXTS`, which rejects a spelling
 * that is not one — so a typo here fails a render in strict mode rather than printing something odd.
 */
export function metricLimitContext(key: MetricRegistryKey, role: MetricFigureRole): string {
  const def = metricDefinition(key);
  switch (def.unit) {
    case 'money':
      return role === 'limit' ? 'money.perDay' : 'money.cost';
    case 'count':
      return 'count';
    case 'pct':
      // A RATE is two decimals (row 9), a SHARE is whole per cent (row 10) — REQ-261 AC2.
      return key === 'clickRatePct' || key === 'conversionRatePct' ? 'pct.rate' : 'pct.share';
    case 'ratio':
      return key === 'roas' ? 'ratio.roas' : 'ratio.frequency';
  }
}

/**
 * Which WIRE TYPE a figure of this metric travels as (API §1.7, 0.3.13 — the `["money","number","pct"]` set
 * a threshold `limit` or an observed `value` declares). The sender's only choice, and it is not really a
 * choice: it follows the metric's unit, with the one wrinkle that return on ad spend is counted in MONEY
 * ("$2.00 back per $1" names a currency) while average times seen is a plain number.
 *
 * It must agree with `metricLimitContext`: the context that comes back has to be one the renderer accepts on
 * a param of this type, or a sentence valid by the table would still refuse to render. `messages.test.ts`
 * asserts the pair for both roles of every alertable metric, so the two cannot drift apart.
 */
export function metricFigureType(key: MetricRegistryKey): 'money' | 'number' | 'pct' {
  switch (metricDefinition(key).unit) {
    case 'money':
      return 'money';
    case 'pct':
      return 'pct';
    case 'count':
      return 'number';
    case 'ratio':
      return key === 'roas' ? 'money' : 'number';
  }
}
